/**
 * A multi-choice field drawn like the CRM's dropdowns: the field opens a list with a search
 * box, numbered rows and a ✓ on what is chosen (picking a row again removes it). The list
 * filters `options`, or asks `search` (2+ characters). What is chosen shows as chips UNDER
 * the field, so fields side by side stay level.
 */
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Check, ChevronDown, Loader2, Search, X } from 'lucide-react';
import { Popover } from '@/pages/appointments/Popover';
import type { Opt } from '@/lib/x-ads/lineItemRules';

export function MultiPicker({ id, selected, onChange, options, search, placeholder, emptyMessage, disabled }: {
  id: string; selected: Opt[]; onChange: (next: Opt[]) => void; options?: Opt[]; search?: (q: string) => Promise<Opt[]>;
  placeholder: string; emptyMessage?: string; disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<Opt[]>([]);
  const [busy, setBusy] = useState(false);
  const searchRef = useRef(search);
  searchRef.current = search;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const s = q.trim();
    if (!searchRef.current) {
      const lower = s.toLowerCase();
      setHits((options ?? []).filter((o) => !lower || o.label.toLowerCase().includes(lower)).slice(0, 300));
      return;
    }
    if (s.length < 2) { setHits([]); setBusy(false); return; }
    let on = true;
    setBusy(true);
    // X returns its own order ("United Arab Emirates" came 11th for that very text): what was typed
    // comes first, then names starting with it, then the rest; countries before regions and cities.
    const lower = s.toLowerCase();
    const rank = (o: Opt) => { const nm = o.label.toLowerCase(); return nm === lower ? 0 : nm.startsWith(lower) ? 1 : nm.includes(lower) ? 2 : 3; };
    const country = (o: Opt) => (/countr/i.test(o.hint ?? '') ? 0 : 1);
    const timer = setTimeout(() => {
      searchRef.current!(s).then((r) => { if (on) setHits([...r].sort((a, b) => rank(a) - rank(b) || country(a) - country(b))); })
        .catch(() => { if (on) setHits([]); }).finally(() => { if (on) setBusy(false); });
    }, 300);
    return () => { on = false; clearTimeout(timer); };
  }, [q, options]);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 0); else setQ(''); }, [open]);

  const chosen = new Set(selected.map((o) => o.value));
  const toggle = (o: Opt) => onChange(chosen.has(o.value) ? selected.filter((x) => x.value !== o.value) : [...selected, o]);
  const waitingForTyping = !!search && q.trim().length < 2;

  return <div className="min-w-0">
    <Popover open={open} onOpenChange={setOpen} className="w-full" panelClassName="w-full min-w-[280px] max-w-[26rem]"
      trigger={
        <button id={id} type="button" role="combobox" aria-expanded={open} aria-controls={`${id}-list`} disabled={disabled} className="xa-field" onClick={() => setOpen((v) => !v)}>
          <span className={clsx('flex min-w-0 items-center gap-2', !selected.length && 'text-text-muted')}>
            <Search size={15} className="shrink-0 opacity-50" />
            <span className="truncate">{selected.length ? `${selected.length} selected` : placeholder}</span>
          </span>
          <ChevronDown size={15} className={clsx('shrink-0 text-text-muted transition-transform duration-200', open && 'rotate-180')} />
        </button>
      }>
      <div className="border-b border-border px-2 py-1.5">
        <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder={search ? 'Type at least 2 letters…' : 'Search…'}
          className="h-8 w-full bg-transparent px-1 text-sm text-text-primary outline-none placeholder:text-text-muted" />
      </div>
      <div id={`${id}-list`} role="listbox" className="max-h-64 overflow-y-auto py-1">
        {busy && <div className="flex items-center gap-2 px-3 py-3 text-sm text-text-muted"><Loader2 size={15} className="animate-spin" />Searching X…</div>}
        {!busy && waitingForTyping && <p className="px-3 py-4 text-center text-[12px] text-text-muted">Type 2 or more letters to search X.</p>}
        {!busy && !waitingForTyping && hits.length === 0 && <p className="px-3 py-4 text-center text-[12px] text-text-muted">{emptyMessage ?? 'No matches.'}</p>}
        {!busy && hits.map((o, i) => {
          const on = chosen.has(o.value);
          return <button key={o.value} type="button" role="option" aria-selected={on} onClick={() => toggle(o)}
            className={clsx('mx-1 flex w-[calc(100%-8px)] items-center rounded-md px-2 py-1.5 text-left text-[13px] transition-colors hover:bg-surface-alt', on && 'bg-primary-soft/60 font-medium')}>
            <span className="mr-2 w-5 shrink-0 text-right text-xs tabular-nums text-text-muted">{i + 1}.</span>
            <span className={clsx('min-w-0 truncate', on && 'text-primary')}>{o.label}</span>
            {o.hint && <span className="ml-auto shrink-0 pl-3 text-[11px] text-text-muted">{o.hint}</span>}
            <Check size={15} className={clsx('shrink-0 transition-opacity', o.hint ? 'ml-2' : 'ml-auto', on ? 'text-primary opacity-100' : 'opacity-0')} />
          </button>;
        })}
      </div>
    </Popover>
    {selected.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5">
      {selected.map((o) => <span key={o.value} className="xa-chip">{o.label}
        <button type="button" aria-label={`Remove ${o.label}`} className="text-text-muted hover:text-text-primary" onClick={() => onChange(selected.filter((x) => x.value !== o.value))}><X size={11} /></button>
      </span>)}
    </div>}
  </div>;
}
